import React, { useEffect } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../../i18n/config';

/**
 * LanguageRouter Component
 * Wrapper component that handles language-based routing
 * Validates language from URL and redirects if invalid
 */
const LanguageRouter = () => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If no language in URL, redirect to default language
    if (!lang) {
      // Try to get preferred language from localStorage
      const storedLang = localStorage.getItem('preferredLanguage');
      const browserLang = navigator.language.split('-')[0];

      // Choose language: stored > browser > default
      const preferredLang =
        (storedLang && SUPPORTED_LANGUAGES.includes(storedLang) ? storedLang : null) ||
        (SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : null) ||
        DEFAULT_LANGUAGE;

      // Redirect to language-prefixed URL
      navigate(`/${preferredLang}${location.pathname}`, { replace: true });
      return;
    }

    // If language is not supported, redirect to default language
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
      console.warn(`Language "${lang}" is not supported. Redirecting to ${DEFAULT_LANGUAGE}`);

      const pathWithoutLang = location.pathname.replace(/^\/[^/]+/, '');
      navigate(`/${DEFAULT_LANGUAGE}${pathWithoutLang}`, { replace: true });
      return;
    }
  }, [lang, navigate, location.pathname]);

  // Don't render until language is validated
  if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
    return null; // or a loading spinner
  }

  // Render child routes
  return <Outlet />;
};

export default LanguageRouter;
